import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Animated as ReactAnimated,
  FlatList,
  Dimensions,
} from 'react-native';
import Animated, {
  Easing,
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { createStackNavigator } from '@react-navigation/stack';
import { isTablet } from 'react-native-device-info';
import { EventRegister } from 'react-native-event-listeners';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import MenuComponent from '../../components/MenuComponent';
import AppContext from '../../navigation/Context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as MenuNavigation from '../../navigation/MenuNavigation';
import colors from '../../resources/colors';
import images from '../../resources/images';
import { useToast } from 'react-native-toast-notifications';
import MenuScreen from '../menu/MenuScreen';
import { render } from 'react-dom';
import { addDays, format, getDay, isSunday, subDays, nextSunday, previousSunday } from 'date-fns';
import { useNavigation } from '@react-navigation/core';
import DataController from '../../controllers/DataController';
import ServiceDropdown from '../../components/ServiceDropdown';
import { FlashList } from '@shopify/flash-list';

const BottomBar = ({ data, showLeft, showRight, handleLeftPress, handleRightPress, width, offset }) => {
  const insets = useSafeAreaInsets();
  const bottomBarStyles = {
    height: insets.bottom + 50,
  };

  const day = data && data[0];
  const week = day && day.WeekTitle;
  const screenWidth = Dimensions.get('screen').width;

  return (
    <Animated.View style={[bottomBarStyles, offset, { width }]}>
      <View style={styles.bottomBar}>
        <View style={styles.bottomBarInnerContainer}>
          {showLeft ? (
            <TouchableOpacity
              style={styles.bottomBarButton}
              hitSlop={styles.bottomBarButtonHitSlop}
              onPress={handleLeftPress}
              activeOpacity={0.5}>
              <Image style={styles.buttonBarButtonImage} source={images.Button_Back} />
              <Text style={styles.buttonBarButtonText} allowFontScaling={false}>
                {'Previous week'}
              </Text>
            </TouchableOpacity>
          ) : (
            <View />
          )}
          {screenWidth > 576 && week && (
            <View style={styles.bottomBarWeek}>
              <Text style={styles.bottomBarWeekText} allowFontScaling={false}>
                {week}
              </Text>
            </View>
          )}
          {showRight ? (
            <TouchableOpacity
              style={styles.bottomBarButton}
              hitSlop={styles.bottomBarButtonHitSlop}
              onPress={handleRightPress}
              activeOpacity={0.5}>
              <Text style={styles.buttonBarButtonText} allowFontScaling={false}>
                {'Next week'}
              </Text>
              <Image style={styles.buttonBarButtonImageRight} source={images.Button_Back} />
            </TouchableOpacity>
          ) : (
            <View />
          )}
        </View>
      </View>
    </Animated.View>
  );
};

const Row = ({ day }) => {
  const navigation = useNavigation();
  const context = useContext(AppContext);
  const { days } = context;
  const dayIndex = days.findIndex((item) => item.id === day.id);

  let background =
    day.Colour && (day.Colour === 'Hangings removed' ? 'grey' : day.Colour.split(/[ |\/]/gmi)[0].toLowerCase());
  const backgroundHex = DataController.colourNameToHex(background);
  const textColor = backgroundHex ? DataController.getTextColor(backgroundHex) : colors.accentWhite;

  return (
    <ServiceDropdown
      open={false}
      day={day}
      dayIndex={dayIndex}
      showDropdowns={true}
    />
  );
};

const renderSeparator = () => <View style={styles.separator} />;

const ThisWeeksScreen = ({ navigation }) => {
  const flatList = useRef(null);
  const toast = useToast();

  const context = useContext(AppContext);
  const { days } = context;

  const [thisWeeksDays, setThisWeeksDays] = useState([]);
  const [showPrevious, setShowPrevious] = useState(true);
  const [showNext, setShowNext] = useState(true);

  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    getDaysForThisWeeks();
  }, []);

  useEffect(() => {
    if (thisWeeksDays.length > 0) {
      const currentSunday = new Date(thisWeeksDays[0].Date)
      __DEV__ && console.log('thisWeeksDays', currentSunday);
      const next = nextSunday(currentSunday);
      const previous = previousSunday(currentSunday);

      const dayIndex = days.findIndex((_day) => _day.Date = DataController.formatDate(currentSunday));
      const previousIndex = days.findIndex((_day) => _day.Date = DataController.formatDate(previous));
      const nextIndex = days.findIndex((_day) => _day.Date = DataController.formatDate(next));

      if (previousIndex !== -1 && previousIndex < dayIndex) {
        setShowPrevious(false);
      } else {
        setShowPrevious(true);
      }

      if (nextIndex !== -1 && nextIndex > dayIndex) {
        setShowNext(false);
      } else {
        setShowNext(true);
      }
    }
  }, [thisWeeksDays]);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <View style={styles.headerRow}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Image style={styles.menuIcon} source={images.Button_Back} />
            <Text style={styles.backButtonText} allowFontScaling={false}>
              {'Back'}
            </Text>
          </TouchableOpacity>
        </View>
      ),
      headerTitle: () => isTablet() || Platform.OS === 'web' ? (
        <Text style={styles.headerTitle} allowFontScaling={false}>
          {'This Week’s Lectionary'}
        </Text>
      ) : <View />,
      headerRight: () => (
        <View />
      ),
      headerTitleAlign: 'center',
      headerStyle: {
        backgroundColor: colors.grey20,
        shadowRadius: 0,
        shadowOffset: {
          height: 0,
        },
        borderBottomWidth: 0.5,
        borderBottomColor: colors.grey50,
        height: Platform.OS === 'web' ? 44 : undefined,
      },
    });
  }, [filterPressed, calendarPressed, navigation]);


  const filterPressed = useCallback(() => {
    toast.show('Not implemented', { id: Date.now() });
  }, [toast]);

  const calendarPressed = useCallback(() => {
    toast.show('Not implemented', { id: Date.now() });
  }, [toast]);

  const getDaysForThisWeeks = (_date) => {
    const date = _date ? _date : new Date();
    const dayOfWeek = getDay(date);
    const daysOfWeek = [];
    const countBackDays = dayOfWeek;

    for (let i = 0; i < 7; i++) {
      if (i === dayOfWeek) {
        daysOfWeek.push(date);
      } else if (i < dayOfWeek) {
        daysOfWeek.push(subDays(date, countBackDays - i));
      } else if (i > dayOfWeek) {
        daysOfWeek.push(addDays(date, i - countBackDays));
      }
    }

    const dates = daysOfWeek.map((_day) => format(_day, 'yyyy-MM-dd'))
    const _days = days.filter((_day) => dates.includes(_day.Date) && _day.id && dates.includes(_day.id.split(':')[0]));

    setThisWeeksDays(_days);
  };

  const renderItem = ({ item, index }) => {
    return <Row day={item} />;
  };

  const keyExtractor = (item, index) => `${item.id}-${index}`;

  const handleLeftPress = () => {
    // console.log('handleLeftPress');
    EventRegister.emit('day/close-dropdowns');

    setTimeout(() => {
      const currentSunday = new Date(thisWeeksDays[0].Date)
      const previous = previousSunday(currentSunday);
      getDaysForThisWeeks(previous);
      flatList.current?.scrollToIndex({ index: 0, animated: true, viewPosition: 0 });
    }, 200);
  };

  const handleRightPress = () => {
    // console.log('handleRightPress');
    EventRegister.emit('day/close-dropdowns');

    setTimeout(() => {
      const currentSunday = new Date(thisWeeksDays[0].Date)
      const next = nextSunday(currentSunday);
      getDaysForThisWeeks(next);
      flatList.current?.scrollToIndex({ index: 0, animated: true, viewPosition: 0 });
    }, 200);
  };

  return (
    <View style={styles.container}>
      <FlashList
        ref={flatList}
        data={thisWeeksDays}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        estimatedItemSize={300}
        ItemSeparatorComponent={renderSeparator}
        initialNumToRender={10}
        contentContainerStyle={{ paddingBottom: 54 }}
        indicatorStyle="black"
      />
      <BottomBar
        data={thisWeeksDays}
        showLeft={showPrevious}
        showRight={showNext}
        handleLeftPress={handleLeftPress}
        handleRightPress={handleRightPress}
        offset={0}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.accentWhite,
    flex: 1,
    height: '100%',
  },
  scrollViewContainer: {
    paddingHorizontal: isTablet() || Platform.OS === 'web' ? 30 : 15,
    // paddingBottom: 54,
    paddingTop: 16,
    width: '100%',
    maxWidth: 1400,
    alignSelf: 'center',
  },
  backButton: {
    flexDirection: 'row',
    marginStart: 4,
    alignItems: 'center',
  },
  backButtonText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 17,
    fontWeight: 'normal',
    color: colors.primary,
    marginStart: -5,
  },
  headerRow: {
    flexDirection: 'row',
  },
  buttonMenu: {
    width: 44,
    height: 44,
    marginStart: 4,
  },
  menuIcon: {
    width: 44,
    height: 44,
    tintColor: colors.primary,
  },
  browseRow: {
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 15,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.grey40,
  },
  separator: {
    // height: 16,
    marginTop: -9,
  },
  title: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 20,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 28,
    letterSpacing: -0.1,
    color: colors.defaultText,
    marginBottom: 4,
    maxWidth: '80%',
  },
  color: {
    position: 'absolute',
    right: 0,
    top: 0,
    borderRadius: 9,
    backgroundColor: '#009900',
    height: 16,
    paddingHorizontal: 6,
    justifyContent: 'center',
    alignSelf: 'center',
  },
  colorText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 12,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: -0.1,
    color: colors.accentWhite,
  },
  caption: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 12,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: 0.2,
    color: colors.defaultText,
  },
  caption2: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 11,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 14,
    letterSpacing: 0.1,
    color: colors.defaultText,
    marginTop: 5,
  },
  bottomContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignSelf: 'flex-end',
  },
  icon: {
    width: 44,
    height: 44,
    marginStart: 5,
  },
  bottomBar: {
    height: 50,
    width: '100%',
    borderTopColor: colors.grey30,
    borderTopWidth: 2,
    overflow: 'visible',
  },
  bottomBarInnerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: 1400,
    alignSelf: 'center',
  },
  bottomBarButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  bottomBarButtonHitSlop: {
    left: 16,
    right: 16,
  },
  buttonBarButtonText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: -0.4,
    color: colors.primary,
  },
  buttonBarButtonImage: {
    height: 44,
    width: 44,
  },
  buttonBarButtonImageRight: {
    height: 44,
    width: 44,
    transform: [{ rotate: '180deg' }],
  },
  bottomBarWeek: {
    height: 50,
    justifyContent: 'center',
  },
  bottomBarWeekText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: -0.4,
    color: colors.defaultText,
  },
  headerTitle: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    textAlign: 'center',
    color: colors.primary,
  },
});

export default ThisWeeksScreen;
